import {
  MiraAvailabilityState,
  MiraPublishedMetric,
  MiraPublishedPeriod,
} from "@/data/models/Mira";
import {
  COLOR_MIRA_ENERGY_IMPLAUSIBLE,
  COLOR_MIRA_ENERGY_IMPLAUSIBLE_DARK,
  COLOR_MIRA_ENERGY_INVALID,
  COLOR_MIRA_ENERGY_INVALID_DARK,
  COLOR_MIRA_ENERGY_UNKNOWN,
  COLOR_MIRA_ENERGY_UNKNOWN_DARK,
  COLOR_MIRA_ENERGY_WITH_READING,
  COLOR_MIRA_ENERGY_WITH_READING_DARK,
  COLOR_MIRA_STATUS_CRITICAL,
  COLOR_MIRA_STATUS_CRITICAL_DARK,
  COLOR_MIRA_STATUS_CRITICAL_TRACK,
  COLOR_MIRA_STATUS_GOOD,
  COLOR_MIRA_STATUS_GOOD_DARK,
  COLOR_MIRA_STATUS_GOOD_TRACK,
  COLOR_MIRA_STATUS_WARNING,
  COLOR_MIRA_STATUS_WARNING_DARK,
  COLOR_MIRA_STATUS_WARNING_TRACK,
  COLOR_MIRA_DELTA_NEUTRAL,
  COLOR_MIRA_DELTA_NEUTRAL_DARK,
  COLOR_MIRA_STATUS_CRITICAL_TRACK_DARK,
  COLOR_MIRA_STATUS_GOOD_TRACK_DARK,
  COLOR_MIRA_STATUS_WARNING_TRACK_DARK,
} from "@/utils/colors";
import {
  MiraCoverageDelta,
  MiraCoverageStatus,
  coverageDelta,
  coverageStatus,
  rankFields,
  shareToPercent,
} from "@/utils/MiraDataQuality";
import { casesText } from "@/utils/MiraCaseUnits";

/**
 * The Datenqualität screen's view model: what each KPI tile, bar and legend row shows, built from a
 * published period and its comparison period.
 *
 * Kept out of the component so it can be tested without mounting anything. Translation and the theme
 * come in through {@link MiraViewContext}, so this module never imports Vuetify or vue-i18n.
 */

/** What the view model needs from its surroundings. */
export interface MiraViewContext {
  t: (key: string, params?: string[]) => string;
  te: (key: string) => boolean;
  formatNumber: (value: number) => string;
  dark: boolean;
  /** The active locale, for month names on the Verlauf timeline. Defaults to German where absent. */
  locale?: string;
}

/** The change shown beside a value, already formatted. */
export interface MiraViewDelta {
  text: string;
  direction: "up" | "down" | "flat";
  /**
   * `neutral` where a rise is neither good nor bad, so the arrow stays grey (Trocknung, Taimoor
   * 2026-09-30: a shorter drying is not automatically a better one). Absent: up is good, as for coverage.
   */
  tone?: "neutral";
  /** The change as a share of the previous value, "(7 %)", shown after {@link text}; see {@link deltaPercentText}. */
  percentText?: string;
}

/**
 * The relative change beside a delta, "(7 %)" (Hans, #2610137: "↑ 7.819 (7 %)"): whole percent, no sign
 * (the arrow carries the direction), and nothing below 1 %. `undefined` where it would mislead: no
 * previous value, or one of zero or below (a signed figure such as an overrun has no meaningful share).
 * Never for a change in percentage points, where a share of a share reads as nonsense; the PP deltas
 * do not ask for it.
 */
export function deltaPercentText(
  current: number | null | undefined,
  previous: number | null | undefined,
  ctx: MiraViewContext
): string | undefined {
  if (current === null || current === undefined || previous === null || previous === undefined || previous <= 0) {
    return undefined;
  }
  const percent = (Math.abs(current - previous) / previous) * 100;
  return percent < 1 ? undefined : `(${ctx.t("mira.format.percent", [ctx.formatNumber(Math.round(percent))])})`;
}

/** The props of a KPI tile. */
export interface MiraTile {
  id: string;
  icon: string;
  title: string;
  value: string;
  caption: string | null;
  delta: MiraViewDelta | null;
  status: MiraCoverageStatus | null;
  statusLabel: string;
  /**
   * The P15 to P85 spread, in the same unit as the tile's median, e.g. "P15 2,6 Tage · P85 65,6 Tage".
   * Only on a tile that shows a median, and only when both percentiles are published. Shown under the
   * caption when the screen allows percentiles (`VIEW_MIRA_PERCENTILES`). `null` by default.
   */
  spread: string | null;
  /** A second figure under the caption, where one tile answers a question with two (#2610137). */
  extraLine?: string | null;
  /** A thin part-to-whole bar at the tile's foot, with a one-line legend (#2610137). */
  parts?: MiraSegment[] | null;
}

/** The props of a bar row. */
export interface MiraBar {
  /**
   * A stable identity for the row, used as its `v-for` key: the underlying id (category id, branch id,
   * field key, bucket key, fleet key …), never the label. Two rows can share a label (every unnamed
   * part reads "Unbekannte Kategorie"), so keying by label raises Vue's "Duplicate keys" warning and
   * lets rows reuse each other's DOM. Required, so a new builder cannot forget it.
   */
  key: string;
  label: string;
  fraction: number;
  valueText: string;
  color: string;
  trackColor: string;
  deltaText: string | null;
  deltaDirection: "up" | "down" | "flat";
  /** Whether the row has a change column: coverage rows do, counts (no comparison) do not. */
  showDelta: boolean;
  /** "120 Projekte": the cases behind a share, shown beside it where the row has no change column (S1). */
  countText?: string | null;
}

/** One part of a stacked bar, already formatted. */
export interface MiraSegment {
  key: string;
  label: string;
  value: number;
  valueText: string;
  shareText: string;
  color: string;
}

const STATUS_COLOR: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: COLOR_MIRA_STATUS_GOOD,
  [MiraCoverageStatus.WARNING]: COLOR_MIRA_STATUS_WARNING,
  [MiraCoverageStatus.CRITICAL]: COLOR_MIRA_STATUS_CRITICAL,
};
const STATUS_COLOR_DARK: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: COLOR_MIRA_STATUS_GOOD_DARK,
  [MiraCoverageStatus.WARNING]: COLOR_MIRA_STATUS_WARNING_DARK,
  [MiraCoverageStatus.CRITICAL]: COLOR_MIRA_STATUS_CRITICAL_DARK,
};

/**
 * The colour of a coverage status: a bar, an icon, an arrow, a legend swatch. One source for every
 * Mira component, so a status reads as one colour wherever it appears in a theme.
 */
export function miraStatusColor(status: MiraCoverageStatus, dark: boolean): string {
  return dark ? STATUS_COLOR_DARK[status] : STATUS_COLOR[status];
}

/** The colour of a change's arrow: grey for a neutral change, else up is good, as for coverage. */
export function miraDeltaColor(direction: "up" | "down" | "flat", dark: boolean, tone?: "neutral"): string {
  if (tone === "neutral") {
    return dark ? COLOR_MIRA_DELTA_NEUTRAL_DARK : COLOR_MIRA_DELTA_NEUTRAL;
  }

  return miraStatusColor(direction === "up" ? MiraCoverageStatus.GOOD : MiraCoverageStatus.CRITICAL, dark);
}
const STATUS_TRACK: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: COLOR_MIRA_STATUS_GOOD_TRACK,
  [MiraCoverageStatus.WARNING]: COLOR_MIRA_STATUS_WARNING_TRACK,
  [MiraCoverageStatus.CRITICAL]: COLOR_MIRA_STATUS_CRITICAL_TRACK,
};
const STATUS_TRACK_DARK: Record<MiraCoverageStatus, string> = {
  [MiraCoverageStatus.GOOD]: COLOR_MIRA_STATUS_GOOD_TRACK_DARK,
  [MiraCoverageStatus.WARNING]: COLOR_MIRA_STATUS_WARNING_TRACK_DARK,
  [MiraCoverageStatus.CRITICAL]: COLOR_MIRA_STATUS_CRITICAL_TRACK_DARK,
};

/** A published metric of a period, or `null` where the period does not publish a value for it. */
export function publishedMetric(
  period: MiraPublishedPeriod | null | undefined,
  metricId: string
): MiraPublishedMetric | null {
  const metric = period?.metrics[metricId];
  return metric?.state === MiraAvailabilityState.AVAILABLE && metric.value !== null && metric.value !== undefined
    ? metric
    : null;
}

/**
 * Whether a figure can apply to the client at all in this period. `NOT_APPLICABLE` is a product
 * boundary, not a gap (plan §5.7), so the screen shows nothing for it rather than "no data": field
 * coverage means nothing to a client with no projects in the period.
 */
export function isApplicable(period: MiraPublishedPeriod | null | undefined, metricId: string): boolean {
  return period?.metrics[metricId]?.state !== MiraAvailabilityState.NOT_APPLICABLE;
}

export function formatPercent(share: number, ctx: MiraViewContext): string {
  const percent = shareToPercent(share);
  const key =
    percent.bound === "below"
      ? "mira.format.percentBelow"
      : percent.bound === "above"
        ? "mira.format.percentAbove"
        : "mira.format.percent";
  return ctx.t(key, [ctx.formatNumber(percent.value)]);
}

/** "+3 Pp.", "−2 Pp.", "±0 Pp.": the sign is always written, so an unchanged value reads as one. */
export function formatDelta(delta: MiraCoverageDelta, ctx: MiraViewContext): MiraViewDelta {
  const sign = delta.direction === "up" ? "+" : delta.direction === "down" ? "−" : "±";
  return {
    text: ctx.t("mira.format.deltaPoints", [`${sign}${ctx.formatNumber(Math.abs(delta.points))}`]),
    direction: delta.direction,
  };
}

/**
 * The P15 to P85 spread line for a tile, in the tile's own unit: "P15 2,6 Tage · P85 65,6 Tage".
 *
 * `null` unless both percentiles are present, so a tile shows a spread only when the figure carries
 * one. Each value goes through the same formatter the tile formats its median with, so the units match.
 */
export function spreadLine(
  p15: number | null | undefined,
  p85: number | null | undefined,
  format: (value: number) => string,
  ctx: MiraViewContext
): string | null {
  if (p15 === null || p15 === undefined || p85 === null || p85 === undefined) {
    return null;
  }

  return ctx.t("mira.format.spread", [format(p15), format(p85)]);
}

/**
 * The median named for the caption beside the headline mean: "Median X" in the tile's own unit, or an
 * empty string where the figure carries no median (a pure count or share). Tiles always lead with the
 * mean since the Kennwert selector was removed (Hans, 2026-10-05); this keeps the median in the caption,
 * exactly as before the selector existed.
 */
export function medianMeasure(
  metric: MiraPublishedMetric | null | undefined,
  format: (value: number) => string,
  ctx: MiraViewContext
): string {
  const median = metric?.median;
  return median === null || median === undefined ? "" : ctx.t("mira.format.median", [format(median)]);
}

/**
 * A caption with its empty parts dropped: captions join their parts with " · ", and a part that is empty
 * (e.g. no median for a figure that has none) would otherwise leave a stray separator, " · über 50 Vorgänge".
 */
export function tidyCaption(caption: string | null): string | null {
  if (caption === null) {
    return null;
  }
  const tidy = caption
    .split(" · ")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(" · ");
  return tidy.length > 0 ? tidy : null;
}

export function metricTitle(metricId: string, ctx: MiraViewContext): string {
  const key = `mira.metrics.${metricId}.title`;
  return ctx.te(key) ? ctx.t(key) : metricId;
}

export function fieldLabel(field: string, ctx: MiraViewContext): string {
  const key = `mira.dataQuality.fields.${field}`;
  return ctx.te(key) ? ctx.t(key) : field;
}

export function caveatText(metric: MiraPublishedMetric | null, ctx: MiraViewContext): string | null {
  const caveat = metric?.caveat;
  const key = caveat ? `mira.caveat.${caveat.key}` : null;
  return key && ctx.te(key) ? ctx.t(key, (caveat?.params ?? []).map(String)) : null;
}

/** A coverage figure's tile: percentage, status, and the change against the comparison period. */
function coverageTile(
  id: string,
  icon: string,
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  caption: (metric: MiraPublishedMetric) => string | null,
  ctx: MiraViewContext
): MiraTile | null {
  const metric = publishedMetric(period, id);
  if (!metric) {
    return null;
  }

  const share = metric.value ?? 0;
  const status = coverageStatus(share);
  const delta = coverageDelta(share, publishedMetric(comparison, id)?.value);

  return {
    id,
    icon,
    title: metricTitle(id, ctx),
    value: formatPercent(share, ctx),
    caption: caption(metric),
    delta: delta ? formatDelta(delta, ctx) : null,
    status,
    statusLabel: ctx.t(`mira.dataQuality.status.${status}`),
    // Coverage figures lead with a share, not a median, so they carry no spread.
    spread: null,
  };
}

/** The Datenqualität KPI tiles, in the order the catalog numbers them (the recognised-insurer share, where published, among the coverage tiles). */
export function buildTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraViewContext
): MiraTile[] {
  const tiles = [
    coverageTile("MIRA-DQ-001", "mdi-text-box-check-outline", period, comparison, (metric) => {
      const weakest = rankFields(metric.breakdown)[0];
      // The projects the coverage is measured over, beside the weakest field (Track #2610072 S1).
      const cases = typeof metric.basis === "number" ? ctx.t("mira.trend.tooltipBasis", [casesText("MIRA-DQ-001", metric.basis, ctx)]) : null;
      const parts = [weakest ? ctx.t("mira.dataQuality.weakestField", [fieldLabel(weakest.field, ctx)]) : null, cases];
      return parts.filter((part): part is string => part !== null).join(" · ") || null;
    }, ctx),
    coverageTile("MIRA-DQ-002", "mdi-flash", period, comparison, (metric) =>
      ctx.t("mira.dataQuality.energyCaption", [ctx.formatNumber(metric.basis ?? 0)]), ctx),
    // The recognised-insurer share (INS-001 feeds the Versicherer card; this capture rate belongs to
    // Datenqualität, as the registry places it). A coverage share like the two above, so it sits with
    // them rather than with the snapshot counts.
    coverageTile("MIRA-INS-003", "mdi-shield-check-outline", period, comparison, (metric) =>
      ctx.t("mira.dataQuality.insurerCaptureCaption", [ctx.formatNumber(metric.basis ?? 0)]), ctx),
  ];

  // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page.
  return tiles.filter((tile): tile is MiraTile => tile !== null && isApplicable(period, tile.id));
}

/**
 * The formatter the Verlauf card and the comparisons use for a Datenqualität figure: the coverage figures
 * and the Zuordnungsquote (INS-003) as a share, anything else as a count.
 */
export function dataQualityTrendFormatter(metricId: string, ctx: MiraViewContext): (value: number) => string {
  return ["MIRA-DQ-001", "MIRA-DQ-002", "MIRA-INS-003"].includes(metricId)
    ? (value) => formatPercent(value, ctx)
    : (value) => ctx.formatNumber(value);
}

/** `MIRA-DQ-001` field by field, weakest first, each in its status colour with its change. */
export function buildFieldRows(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraViewContext
): MiraBar[] {
  const metric = publishedMetric(period, "MIRA-DQ-001");
  const previous = publishedMetric(comparison, "MIRA-DQ-001");

  return rankFields(metric?.breakdown).map((entry) => {
    const status = coverageStatus(entry.share);
    const delta = coverageDelta(entry.share, previous?.breakdown?.[entry.field]);
    const formatted = delta ? formatDelta(delta, ctx) : null;

    return {
      key: entry.field,
      label: fieldLabel(entry.field, ctx),
      fraction: entry.share,
      valueText: formatPercent(entry.share, ctx),
      color: miraStatusColor(status, ctx.dark),
      trackColor: ctx.dark ? STATUS_TRACK_DARK[status] : STATUS_TRACK[status],
      deltaText: formatted?.text ?? null,
      deltaDirection: formatted?.direction ?? "flat",
      showDelta: true,
    };
  });
}

/** The legend under the field bars: what each status colour means. */
export function buildStatusLegend(ctx: MiraViewContext): Array<{ label: string; color: string }> {
  return [
    { label: ctx.t("mira.dataQuality.legend.good"), color: miraStatusColor(MiraCoverageStatus.GOOD, ctx.dark) },
    { label: ctx.t("mira.dataQuality.legend.warning"), color: miraStatusColor(MiraCoverageStatus.WARNING, ctx.dark) },
    { label: ctx.t("mira.dataQuality.legend.critical"), color: miraStatusColor(MiraCoverageStatus.CRITICAL, ctx.dark) },
  ];
}

/**
 * `MIRA-DQ-002` as parts of the ended assignments: with readings, without, invalid, implausible.
 * The four add up to the basis; `onImplausibleProject` does not belong to the whole (see
 * {@link energyImplausibleProjectsNote}).
 */
export function buildEnergySegments(period: MiraPublishedPeriod | null, ctx: MiraViewContext): MiraSegment[] {
  const metric = publishedMetric(period, "MIRA-DQ-002");
  const breakdown = metric?.breakdown;
  const basis = metric?.basis ?? 0;
  if (!breakdown || basis === 0) {
    return [];
  }

  return [
    { key: "withReading", color: ctx.dark ? COLOR_MIRA_ENERGY_WITH_READING_DARK : COLOR_MIRA_ENERGY_WITH_READING },
    { key: "unknownReading", color: ctx.dark ? COLOR_MIRA_ENERGY_UNKNOWN_DARK : COLOR_MIRA_ENERGY_UNKNOWN },
    { key: "invalidReading", color: ctx.dark ? COLOR_MIRA_ENERGY_INVALID_DARK : COLOR_MIRA_ENERGY_INVALID },
    { key: "implausibleReading", color: ctx.dark ? COLOR_MIRA_ENERGY_IMPLAUSIBLE_DARK : COLOR_MIRA_ENERGY_IMPLAUSIBLE },
  ]
    .filter((segment) => breakdown[segment.key] !== undefined)
    .map((segment) => ({
      key: segment.key,
      label: ctx.t(`mira.dataQuality.energy.${segment.key}`),
      value: breakdown[segment.key],
      valueText: ctx.formatNumber(breakdown[segment.key]),
      shareText: formatPercent(breakdown[segment.key] / basis, ctx),
      color: segment.color,
    }));
}

/** Counted beside the whole, never inside it: those readings were captured, only their verdict differs. */
export function energyImplausibleProjectsNote(period: MiraPublishedPeriod | null, ctx: MiraViewContext): string | null {
  const count = publishedMetric(period, "MIRA-DQ-002")?.breakdown?.onImplausibleProject;
  return count ? ctx.t("mira.dataQuality.energy.onImplausibleProject", [ctx.formatNumber(count)]) : null;
}
